import type { Song, GenerateSetlistOptions } from '@/types'

interface GeneratedItem {
  song: Song
  reason: string
}

export function generateSetlist(
  songs: Song[],
  options: GenerateSetlistOptions,
): GeneratedItem[] {
  const { targetDurationMinutes, minimumRating, preferLessPlayed, allowDuplicates, excludeSongIds } = options
  const targetSeconds = targetDurationMinutes * 60
  const excludeSet = new Set(excludeSongIds ?? [])

  // Filter eligible songs
  const eligible = songs.filter((s) => s.rating >= minimumRating && !excludeSet.has(s.id))
  if (eligible.length === 0) return []

  // Score songs: lower play count = higher weight when preferLessPlayed
  const maxCount = Math.max(...eligible.map((s) => s.setlist_count), 1)
  const scored = eligible.map((s) => {
    let weight = 1
    if (preferLessPlayed) {
      weight = maxCount - s.setlist_count + 1
    }
    return { song: s, weight }
  })

  // Weighted random selection
  const selected: GeneratedItem[] = []
  const usedIds = new Set<string>()
  let totalDuration = 0
  let attempts = 0
  const maxAttempts = eligible.length * 10

  while (totalDuration < targetSeconds && attempts < maxAttempts) {
    attempts++

    const totalWeight = scored
      .filter((s) => allowDuplicates || !usedIds.has(s.song.id))
      .reduce((sum, s) => sum + s.weight, 0)

    if (totalWeight === 0) break

    let rand = Math.random() * totalWeight
    let chosen: typeof scored[0] | null = null

    for (const item of scored) {
      if (!allowDuplicates && usedIds.has(item.song.id)) continue
      rand -= item.weight
      if (rand <= 0) {
        chosen = item
        break
      }
    }

    if (!chosen) break

    const songDuration = chosen.song.performance_duration_seconds ?? chosen.song.duration_seconds ?? 180
    if (totalDuration + songDuration > targetSeconds + 60) {
      // Allow up to 60s overshoot
      if (totalDuration + songDuration > targetSeconds + 120) continue
    }

    const reasons: string[] = []
    if (preferLessPlayed && chosen.song.setlist_count < maxCount) {
      reasons.push(`Played ${chosen.song.setlist_count} times (less than avg)`)
    }
    if (chosen.song.rating >= 4) {
      reasons.push(`High rating: ${chosen.song.rating}★`)
    }
    reasons.push('Random selection')

    selected.push({ song: chosen.song, reason: reasons[0] })
    usedIds.add(chosen.song.id)
    totalDuration += songDuration
  }

  return selected
}

export function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

export function totalDurationSeconds(songs: { performance_duration_seconds: number | null; duration_seconds: number | null }[]): number {
  return songs.reduce((sum, s) => sum + (s.performance_duration_seconds ?? s.duration_seconds ?? 180), 0)
}
